import React, { useState, useEffect } from 'react';
import { PrimeProcess, PrimeArtifactStateTransition } from '../../../types/primeModel';
import { PrimeArtifact } from '../../../types/primeArtifact';
import { ArtifactService } from '../../../services/api/artifactService';
import { ProcessService } from '../../../services/api/processService';
import { PrimeArtifactService } from '../../../services/primeArtifactService';
import TransitionCreationFlow from '../TransitionCreationFlow';
import './ProcessPage.css';
import { useArtifactTree } from '../../../contexts/ArtifactTreeContext';
import { ArtifactItemState } from '../../../types/artifactItems';
import { useUser } from '../../../contexts/UserContext';

interface ProcessTransition extends PrimeArtifactStateTransition {
  artifact: PrimeArtifact;
  from_state_name: string;
  to_state_name: string;
  artifact_name?: string;
}

interface ProcessWithArtifact {
  process: PrimeProcess;
  artifact: PrimeArtifact | undefined;
}

interface ProcessWithTransitions extends PrimeProcess {
  transitions: ProcessTransition[];
}

const ProcessPage: React.FC = () => {
  const { selectedTree } = useArtifactTree();
  const { selectedCompany } = useUser();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProcess, setSelectedProcess] = useState<ProcessWithTransitions | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [processesWithArtifacts, setProcessesWithArtifacts] = useState<ProcessWithArtifact[]>([]);
  const [filteredProcesses, setFilteredProcesses] = useState<ProcessWithArtifact[]>([]);
  const [showTransitionCreation, setShowTransitionCreation] = useState(false);
  const [selectedTransition, setSelectedTransition] = useState<ProcessTransition | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedTransition, setEditedTransition] = useState<Partial<ProcessTransition> | null>(null);
  const [artifactFilter, setArtifactFilter] = useState<string>('');

  // Fetch processes and artifacts when component mounts
  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch all artifacts first
        const artifactsData = await ArtifactService.getAll(
          selectedTree?.id || '',
          undefined,
          selectedCompany?.id
        );

        // Then fetch processes for each artifact
        const allProcesses: PrimeProcess[] = [];
        for (const artifact of artifactsData) {
          try {
            const processes = await ProcessService.getByArtifactId(artifact.id);
            allProcesses.push(...processes);
          } catch (err) {
            console.error(`Error fetching processes for artifact ${artifact.id}:`, err);
            // Continue with other artifacts even if one fails
          }
        }

        // Combine processes with their artifacts
        const processesWithArtifactsData: ProcessWithArtifact[] = allProcesses.map((process) => {
          const artifactData = artifactsData.find((a) => a.id === process.prime_artifact_ref);
          const artifact: PrimeArtifact | undefined = artifactData
            ? {
                id: artifactData.id,
                name: artifactData.name,
                description: artifactData.description || undefined,
                artifact_type_id: artifactData.artifact_type_id || 'unknown',
                artifact_type_name: '', // We'll need to fetch this separately
                parent_ref: artifactData.parent_ref || undefined,
                parent_name: '', // We'll need to fetch this separately
                children: [],
                created_at: artifactData.created_at || new Date().toISOString(),
                updated_at: artifactData.updated_at || undefined,
                prime_artifact_id: artifactData.prime_artifact_id || undefined,
              }
            : undefined;
          return { process, artifact };
        });

        setProcessesWithArtifacts(processesWithArtifactsData);
        setFilteredProcesses(processesWithArtifactsData);
      } catch (err) {
        console.error('Error loading initial data:', err);
        setError('Failed to load processes. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    void loadInitialData();
  }, [selectedTree?.id, selectedCompany?.id]);

  // Filter processes based on search term and artifact filter
  useEffect(() => {
    let filtered = [...processesWithArtifacts];

    if (searchTerm) {
      filtered = filtered.filter(
        (processWithArtifact) =>
          processWithArtifact.process.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          processWithArtifact.process.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (artifactFilter) {
      filtered = filtered.filter(
        (processWithArtifact) => processWithArtifact.artifact?.id === artifactFilter
      );
    }

    setFilteredProcesses(filtered);
  }, [searchTerm, artifactFilter, processesWithArtifacts]);

  const handleProcessSelect = async (processWithArtifact: ProcessWithArtifact) => {
    try {
      setLoading(true);

      // Fetch transitions for the selected process
      const transitions = await PrimeArtifactService.getPrimeArtifactTransitions(
        processWithArtifact.process.id
      );

      // Create a process with transitions
      const processWithTransitions: ProcessWithTransitions = {
        ...processWithArtifact.process,
        transitions: (await Promise.all(
          transitions.map(async (transition: PrimeArtifactStateTransition) => {
            const artifactData = await ArtifactService.getById(transition.prime_artifact_ref);
            const states = await ArtifactService.getStates(transition.prime_artifact_ref);

            const fromState = states.find(
              (s: ArtifactItemState) => s.id === transition.from_state_ref
            );
            const toState = states.find((s: ArtifactItemState) => s.id === transition.to_state_ref);

            if (!artifactData) {
              console.error('No artifact data available for transition');
              return null;
            }

            const artifact: PrimeArtifact = {
              id: artifactData.id,
              name: artifactData.name,
              description: artifactData.description || undefined,
              artifact_type_id: artifactData.artifact_type_id || 'unknown',
              artifact_type_name: '', // We'll need to fetch this separately
              parent_ref: artifactData.parent_ref || undefined,
              parent_name: '', // We'll need to fetch this separately
              children: [],
              created_at: artifactData.created_at || new Date().toISOString(),
              updated_at: artifactData.updated_at || undefined,
              prime_artifact_id: artifactData.prime_artifact_id || undefined,
            };

            const processTransition: ProcessTransition = {
              ...transition,
              artifact,
              from_state_name: (fromState as ArtifactItemState)?.name || '',
              to_state_name: (toState as ArtifactItemState)?.name || '',
            };

            return processTransition;
          })
        )).filter((transition): transition is ProcessTransition => transition !== null),
      };

      setSelectedProcess(processWithTransitions);
      setSelectedTransition(null);
      setEditedTransition(null);
      setIsEditing(false);
    } catch (err) {
      console.error('Error fetching process transitions:', err);
      setError('Failed to fetch process transitions');
    } finally {
      setLoading(false);
    }
  };

  const handleTransitionCreated = async (newTransition: PrimeArtifactStateTransition) => {
    try {
      setLoading(true);

      // Fetch the artifact to get its name
      const artifactData = await ArtifactService.getById(newTransition.prime_artifact_ref);

      // Fetch the states
      const states = await ArtifactService.getStates(newTransition.prime_artifact_ref);

      // Find the state names from the states array
      const fromState = states.find(
        (s: ArtifactItemState) => s.id === newTransition.from_state_ref
      );
      const toState = states.find((s: ArtifactItemState) => s.id === newTransition.to_state_ref);

      // Create a proper ProcessTransition object
      if (!artifactData) {
        console.error('No artifact data available for new transition');
        return;
      }

      const artifact: PrimeArtifact = {
        id: artifactData.id,
        name: artifactData.name,
        description: artifactData.description || undefined,
        artifact_type_id: artifactData.artifact_type_id || 'unknown',
        artifact_type_name: '', // We'll need to fetch this separately
        parent_ref: artifactData.parent_ref || undefined,
        parent_name: '', // We'll need to fetch this separately
        children: [],
        created_at: artifactData.created_at || new Date().toISOString(),
        updated_at: artifactData.updated_at || undefined,
        prime_artifact_id: artifactData.prime_artifact_id || undefined,
      };

      const processTransition: ProcessTransition = {
        ...newTransition,
        artifact,
        from_state_name: (fromState as ArtifactItemState)?.name || '',
        to_state_name: (toState as ArtifactItemState)?.name || '',
      };

      // Add the new transition to the list
      if (selectedProcess) {
        setSelectedProcess({
          ...selectedProcess,
          transitions: [...selectedProcess.transitions, processTransition],
        });
      }

      // Hide the transition creation flow
      setShowTransitionCreation(false);
    } catch (err) {
      console.error('Error processing new transition:', err);
      setError('Failed to process the new transition. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const handleTransitionSelect = (transition: ProcessTransition) => {
    setSelectedTransition(transition);
    setEditedTransition({ ...transition });
    setIsEditing(false);
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedTransition(selectedTransition ? { ...selectedTransition } : null);
  };

  const handleSaveTransition = async () => {
    if (!editedTransition || !selectedTransition) return;

    try {
      // Here you would call an API to update the transition
      // For now, we'll just update the local state
      const updatedTransition = { ...selectedTransition, ...editedTransition };

      // Update the transition in the selected process
      if (selectedProcess) {
        const updatedProcess = { ...selectedProcess };
        const transitionIndex = updatedProcess.transitions.findIndex(
          (t: ProcessTransition) => t.id === selectedTransition.id
        );
        if (transitionIndex !== -1) {
          updatedProcess.transitions[transitionIndex] = updatedTransition;
          setSelectedProcess(updatedProcess);

          // Also update in the processes list
          const updatedProcesses = processesWithArtifacts.map((p) =>
            p.process.id === selectedProcess.id ? { ...p, process: updatedProcess } : p
          );
          setProcessesWithArtifacts(updatedProcesses);
          setFilteredProcesses(updatedProcesses);
        }
      }

      setSelectedTransition(updatedTransition);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving transition:', error);
      // Handle error (show error message, etc.)
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    if (!editedTransition) return;

    const { name, value } = e.target;
    setEditedTransition({
      ...editedTransition,
      [name]: value,
    });
  };

  // Group transitions by type
  const groupedTransitions =
    selectedProcess?.transitions.reduce(
      (groups, transition) => {
        const type = transition.default_transition_type;
        if (!groups[type]) {
          groups[type] = [];
        }
        groups[type].push(transition);
        return groups;
      },
      {} as Record<string, ProcessTransition[]>
    ) || {};

  return (
    <div className="process-page">
      <div className="page-header">
        <h1>Process Management</h1>
        <p>View and manage business processes and their transitions</p>
      </div>

      <div className="process-page-content">
        <div className="process-list-container">
          <div className="search-controls">
            <input
              type="text"
              placeholder="Search processes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <select value={artifactFilter} onChange={(e) => setArtifactFilter(e.target.value)}>
              <option value="">All Artifacts</option>
              {Array.from(
                new Set(processesWithArtifacts.map((p) => p.artifact?.id).filter(Boolean))
              ).map((artifactId) => {
                const artifact = processesWithArtifacts.find((p) => p.artifact?.id === artifactId);
                return (
                  <option key={artifactId} value={artifactId}>
                    {artifact?.artifact?.name || artifactId}
                  </option>
                );
              })}
            </select>
          </div>

          <div className="process-list">
            {loading ? (
              <div className="loading-message">Loading processes...</div>
            ) : error ? (
              <div className="error-message">{error}</div>
            ) : filteredProcesses.length === 0 ? (
              <div className="no-results">No processes found</div>
            ) : (
              filteredProcesses.map((processWithArtifact) => (
                <div
                  key={processWithArtifact.process.id}
                  className={`process-list-item ${selectedProcess?.id === processWithArtifact.process.id ? 'selected' : ''}`}
                  onClick={() => handleProcessSelect(processWithArtifact)}
                >
                  <div className="process-name">{processWithArtifact.process.name}</div>
                  <div className="artifact-name">
                    {processWithArtifact.artifact?.name || 'Unknown Artifact'}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="process-details-container">
          {selectedProcess ? (
            <div className="process-details">
              <div className="process-header">
                <div>
                  <h2>{selectedProcess.name}</h2>
                  <p>{selectedProcess.description}</p>
                </div>
                <button
                  className="add-transition-button"
                  onClick={() => setShowTransitionCreation(true)}
                >
                  Add New Transition
                </button>
              </div>

              {showTransitionCreation && (
                <TransitionCreationFlow
                  process={selectedProcess}
                  onTransitionCreated={handleTransitionCreated}
                  onCancel={() => setShowTransitionCreation(false)}
                  skipProcessSelection={true}
                />
              )}

              <div className="transitions-section">
                {Object.entries(groupedTransitions).map(([type, transitions]) => (
                  <div key={type} className="transition-group">
                    <h4>{type}</h4>
                    <div className="transition-list">
                      {transitions.map((transition) => (
                        <div
                          key={transition.id}
                          className={`transition-item ${selectedTransition?.id === transition.id ? 'selected' : ''}`}
                          onClick={() => handleTransitionSelect(transition)}
                        >
                          <div className="transition-header">
                            <span className="artifact-name">
                              {transition.artifact.name || 'Unknown Artifact'}
                            </span>
                          </div>
                          <div className="transition-states">
                            <span className="from-state">{transition.from_state_name}</span>
                            <span className="arrow">→</span>
                            <span className="to-state">{transition.to_state_name}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {selectedTransition && (
                <div className="transition-details-form">
                  <h3>Transition Details</h3>
                  {isEditing ? (
                    <div className="edit-form">
                      <div className="form-group">
                        <label htmlFor="artifact_name">Artifact Name</label>
                        <input
                          type="text"
                          id="artifact_name"
                          name="artifact_name"
                          value={editedTransition?.artifact_name || ''}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="from_state">From State</label>
                        <input
                          type="text"
                          id="from_state"
                          name="from_state_name"
                          value={editedTransition?.from_state_name || ''}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="to_state">To State</label>
                        <input
                          type="text"
                          id="to_state"
                          name="to_state_name"
                          value={editedTransition?.to_state_name || ''}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="transition_type">Transition Type</label>
                        <select
                          id="transition_type"
                          name="default_transition_type"
                          value={editedTransition?.default_transition_type || ''}
                          onChange={handleInputChange}
                        >
                          <option value="">Select Type</option>
                          <option value="PROCESSITEM">Process Item</option>
                          <option value="MATERIAL">Material</option>
                          <option value="RESOURCE">Resource</option>
                          <option value="PROCESSITEM_PERSISTENT">Process Item Persistent</option>
                          <option value="CREATION">Creation</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label htmlFor="description">Description</label>
                        <textarea
                          id="description"
                          name="description"
                          value={editedTransition?.description || ''}
                          onChange={handleInputChange}
                          rows={3}
                        />
                      </div>
                      <div className="form-actions">
                        <button className="save-button" onClick={handleSaveTransition}>
                          Save
                        </button>
                        <button className="cancel-button" onClick={handleCancelEdit}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="view-details">
                      <div className="detail-row">
                        <span className="detail-label">Artifact:</span>
                        <span className="detail-value">
                          {selectedTransition.artifact.name || 'Unknown Artifact'}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">From State:</span>
                        <span className="detail-value">{selectedTransition.from_state_name}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">To State:</span>
                        <span className="detail-value">{selectedTransition.to_state_name}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Type:</span>
                        <span className="detail-value">
                          {selectedTransition.default_transition_type || 'Not specified'}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Description:</span>
                        <span className="detail-value">
                          {selectedTransition.description || 'No description provided'}
                        </span>
                      </div>
                      <div className="form-actions">
                        <button className="edit-button" onClick={handleEditClick}>
                          Edit
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="no-process-selected">Select a process to view details</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProcessPage;
